/**
 * Controlled fields for the status-changed-to-hold dialog.
 *
 * Must render as a child of Modal so {@link useModalPortalTarget} resolves the
 * portal node and SearchableSelect can mount options above the scrollable
 * body. The date `InputField` parses `mm/dd/yyyy` and ignores invalid
 * strings; a required free-text field appears when "Other" is picked.
 *
 * @module statusChangedToHoldModalBody
 */
import {
  InputField,
  SearchableSelect,
  useModalPortalTarget,
} from "@risalabs_frontend_org/oasis-ui-kit";
import moment from "moment";

/** Select options for why an order is being placed on hold. */
const HOLD_REASON_OPTIONS = [
  "Prior DOS being worked by Onshore",
  "Prior DOS dosage currently not given",
  "Clinical Document(s) Pending to be received",
  "Patient Plan not active currently",
  "Payer Policy Changes",
  "Medicaid Secondary Ins",
  "Other",
].map((label) => ({ label, value: label }));

/**
 * Props for {@link StatusChangedToHoldModalBody}.
 */
export interface StatusChangedToHoldModalBodyProps {
  /** Currently selected hold-until date, or null before the user picks one. */
  date: Date | null;
  /** Called with a parsed Date when the date field emits a valid value. */
  onDateChange: (date: Date) => void;
  /** Currently selected hold reason, empty string before a pick. */
  reason: string;
  /** Called with the picked hold reason. */
  onReasonChange: (reason: string) => void;
  /** Free-text reason entered when "Other" is selected. */
  otherReason: string;
  /** Called on every edit of the free-text "Other" reason. */
  onOtherReasonChange: (otherReason: string) => void;
}

/**
 * Date picker and hold-reason select for {@link StatusChangedToHoldModal}.
 *
 * Leaving "Other" clears the free-text field so a stale value cannot be
 * saved under a different reason.
 *
 * @param props - Current form values and change handlers
 * @returns Form fields for hold date, reason, and optional Other text
 */
const StatusChangedToHoldModalBody = ({
  date,
  onDateChange,
  reason,
  onReasonChange,
  otherReason,
  onOtherReasonChange,
}: StatusChangedToHoldModalBodyProps) => {
  const portalRef = useModalPortalTarget();

  return (
    <div className="flex flex-col gap-2">
      <InputField
        id="date"
        label=" Please select the date upto which the order should be on hold"
        placeholder="Select a date"
        size="md"
        fullWidth
        type="date"
        dateFormat="mm/dd/yyyy"
        value={date ? moment(date).format("MM/DD/YYYY") : ""}
        onDateChange={(formattedDate) => {
          const parsed = moment(formattedDate, "MM/DD/YYYY", true);
          if (parsed.isValid()) {
            onDateChange(parsed.toDate());
          }
        }}
        required
      />
      <SearchableSelect
        id="hold-reason"
        label="Reason for Hold"
        placeholder="Select a reason"
        options={HOLD_REASON_OPTIONS}
        value={reason || undefined}
        onOptionChange={(option) => {
          onReasonChange(option.value);
          if (option.value !== "Other") {
            onOtherReasonChange("");
          }
        }}
        usePortal
        targetRef={portalRef}
      />
      {reason === "Other" && (
        <InputField
          id="other-reason"
          label="Specify the reason"
          placeholder="Enter the reason"
          size="md"
          fullWidth
          value={otherReason}
          onChange={(e) => onOtherReasonChange(e.target.value)}
          required
        />
      )}
    </div>
  );
};

export default StatusChangedToHoldModalBody;
